<template>
    <modals-side-generic
    v-if="noCameraAccess && !showQRCodeLayer"
    :triggerClose="triggerClose"
    :title="title"
    :subTitle="t('modals.qrCode.subTitle')"
    @hideMe="emit('hideMe')">
        <!-- No Camera Access Empty State -->
        <empty-state-generic
        hasButton
        illustration="emptyState/camara-no-access"
        :title="t('modals.qrCode.noCameraAccessEmptyState.title')"
        :description="t('modals.qrCode.noCameraAccessEmptyState.description')"
        :buttonLabel="t('modals.qrCode.noCameraAccessEmptyState.buttonText')"
        @clicked="openAppSettings"/>
    </modals-side-generic>
    <!-- Feedback Toast -->
    <toast-generic
    v-if="showFeedbackToast"
    :toastType="feedbackToastData.toastType"
    :feedbackText="feedbackToastData.text"
    :buttonsParams="feedbackToastData.buttons"
    @hideMe="showFeedbackToast = false"/>
    <!-- QR Code Layer -->
    <teleport to="#layer-for-camera">
        <div v-if="showQRCodeLayer && !noCameraAccess" class="qr-code-layer">
            <!-- Header -->
            <div
            class="header"
            :class="{ 'mobile-status-bar-android': getDeviceInUse === 'android', 'mobile-status-bar-ios': getDeviceInUse === 'ios' }">
                <div class="relative-content-helper">
                    <button-tertiary
                    class="back-icon"
                    buttonStyle="round"
                    icon="arrow-left-full"
                    @clicked="closeModal()"/>
                    <div class="title-wrapper">
                        <div class="title">{{title}}</div>
                        <div class="sub-title">{{t('modals.qrCode.subTitle')}}</div>
                    </div>
                </div>
            </div>
            <!-- Lens -->
            <div class="qr-lens"/>
            <!-- QR Code Message -->
            <div class="message-wrapper"> {{t('modals.qrCode.message')}}</div>
        </div>
    </teleport>
</template>

<script setup>
import ModalsSideGeneric from '@/components/generic/modals/side/Generic'
import ButtonTertiary from '@/components/generic/buttons/Tertiary'
import EmptyStateGeneric from '@/components/generic/emptyStates/Generic'
import ToastGeneric from '@/components/generic/toasts/Generic'

import { ref, onBeforeUnmount } from 'vue'
/* Store */
import { mapActions, mapGetters } from '@/store/helpers/mappers'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

const { updatePreventMobileFeatureBack, updateApplyTransparencyToApp } = mapActions()
const { getPreventMobileSideModalBackState, getDeviceInUse } = mapGetters()

/* Emmitters */
const emit = defineEmits(['contactSelected', 'hideMe'])

/* Props
@title {String} - text to display in title
@subTitle {String} - text to display in subTitle
*/
const props = defineProps({
    title: String,
    subTitle: String
})

// QR Code Scanner Logic
const noCameraAccess = ref(false)
const showQRInfoModal = ref(false)
const showQRCodeLayer = ref(false)

const initialPermissionCheck = () => {
    noCameraAccess.value = false

    window.QRScanner.getStatus(function (status) {
        console.log('initialPermissionCheck status =>', status)
        if (status.authorized) {
            // Authorized Access Case
            initializeScannerLayer()
        } else if (status.denied) {
            // Denied Access Case
            noAccessHandler()
        } else {
            /* Didnt request permissions yet */
            window.QRScanner.prepare(checkQRPermissionStatus)
        }
    })
}

const initializeScannerLayer = () => {
    // Make Rest of App transparent
    updateApplyTransparencyToApp(true)
    // Show the QR Code Layer
    showQRCodeLayer.value = true
    // Display Camera Image
    window.QRScanner.show()
    // Start Scan
    window.QRScanner.scan(displayContents)
    /* We need to check if user has selected "Don't show again" before we show QR Code Info Modal */
    if (!window.localStorage.getItem('dontShowQRModal')) {
        window.localStorage.setItem('dontShowQRModal', 'false')
    } else if (window.localStorage.getItem('dontShowQRModal') === 'false') {
        showQRInfoModal.value = true
    }
}

const noAccessHandler = () => {
    noCameraAccess.value = true
    showQRCodeLayer.value = false
}

const openAppSettings = () => {
    showQRCodeLayer.value = false

    /* We need to open app permissions */
    window.cordova.plugins.settings.open('settings', openSettingsSucess, openSettingsError)
}

const openSettingsSucess = $event => {
    console.log($event, 'sucess')
}

const openSettingsError = $event => {
    feedbackToastData.value = {
		toastType: 'error',
		text: t('genericErrorMessage'),
		buttons: [
			{ text: t('commonButtons.tryAgain'), callBackAction: openAppSettings }
		]
	}
	showFeedbackToast.value = true
}

const checkQRPermissionStatus = (err, status) => {
    noCameraAccess.value = false

    if (err) {
        console.error(err)
        if (err.code === 1) {
            // code 1 === CAMERA_ACCESS_DENIED
            noAccessHandler()
        }
    } else if (status.authorized) {
        // Authorized Access Case
        initializeScannerLayer()
    } else {
        // No Access Case
        // Can be Denied, Restricted or non-existant
        noAccessHandler()
    }
}

const displayContents = (err, text) => {
    if (err) {
        // an error occurred, or the scan was canceled (error code `6`)
        console.error(err)
    } else {
        // The scan completed, display the contents of the QR code:
        // QR Code result will be a string ex("BAI|5|212122112|COUTINHO ARMANDO CABRAL BUTOTO")
        // TODO: Make sure this is always the response format

        // Regular expression pattern to match the expected format
        const qrCodePattern = /^([^|]+)\|([^|]+)\|([^|]+)\|([^|]+)$/

        // Check if the QR code output matches the expected format
        if (qrCodePattern.test(text)) {
            // Extract the parts of the QR code output
            const [, companyName, id, phoneNumber, userName] = text.match(qrCodePattern)

            // Log the extracted phoneNumber
            console.log('Phone Number:', phoneNumber)

            // Hide Camera Layer and remove transparency from rest of the App
            window.QRScanner.hide()
            updateApplyTransparencyToApp(false)
            showQRCodeLayer.value = false

            emit('qrCodeComplete', phoneNumber)
        } else {
            // Show Error Toast
            errorHandler()
        }
    }
}

initialPermissionCheck()

/***********************************************************
*                                                          *
*                 MOBILE NATIVE BACK LOGIC                 *
*                                                          *
***********************************************************/
/* Preventing mobile back to trigger features close */
updatePreventMobileFeatureBack(true)

onBeforeUnmount(() => {
	updatePreventMobileFeatureBack(false)
})

const closeModal = () => {
    if (getPreventMobileSideModalBackState.value) return
    window.QRScanner.cancelScan()
    window.QRScanner.hide()
    updateApplyTransparencyToApp(false)
    showQRCodeLayer.value = false
    emit('hideMe')
}

document.addEventListener('backbutton', closeModal, false)

/*************************************************
*                                                *
*             Success/Error Handlers             *
*                                                *
*************************************************/
const showFeedbackToast = ref(false)
const feedbackToastData = ref({
	toastType: '',
	text: '',
	buttons: []
})

const errorHandler = data => {
	feedbackToastData.value = {
		toastType: 'error',
		text: t('modals.qrCode.errorToast.text'),
		buttons: [
			{ text: t('modals.qrCode.errorToast.buttonText'), callBackAction: window.QRScanner.scan(displayContents) }
		]
	}
	showFeedbackToast.value = true
}
</script>

<style lang="scss" scoped>
.qr-code-layer {
    position: absolute;
    inset: 0;

    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;

    .header {
        position: absolute;
        top: 0;
        z-index: 99999999;

        width: 100%;
        height: 76px;

        display: flex;
        justify-content: flex-start;

        background-image: var(--gradient-horizontal);

        &.mobile-status-bar-android {
            height: calc(76px + var(--android-top-padding));
            padding-top: calc(16px + var(--android-top-padding));
        }

        &.mobile-status-bar-ios {
            height: calc(76px + constant(safe-area-inset-top));
            height: calc(76px + env(safe-area-inset-top));

            padding-top: calc(16px + constant(safe-area-inset-top));
            padding-top: calc(16px + env(safe-area-inset-top));
        }

        .relative-content-helper {
            width: 100%;
            height: 40px;
            position: relative;

            .back-icon {
                width: 40px;
                height: 40px;

                position: absolute;
                left: 16px;

                svg {
                    :deep(.change-color) {
                        stroke: var(--white);
                    }
                }
            }

            .title-wrapper {
                width: 100%;
                height: 40px;
                padding: 0 40px;

                display: flex;
                flex-direction: column;
                justify-content: center;
                text-align: center;

                .title {
                    width: 100%;
                    height: 13px;

                    @include H8-Regular;
                    line-height: 13px;
                    color: #EDEDED;
                }

                .sub-title {
                    width: 100%;
                    height: 20px;
                    min-height: 20px;

                    @include H5-Medium;
                    line-height: 20px;
                    color: #FFFFFF;
                }
            }
        }
    }

    .qr-lens {
        width: 229px;
        height: 229px;
        background-color: transparent;
        border-radius: 12px;
        opacity: 0.5;
        box-shadow: 0 0 0 99999px rgba(0, 0, 0, 1);
    }

    .message-wrapper {
        position: absolute;
        bottom: 30%;

        @include H7-Regular;
        color: #EDEDED;
        line-height: 16px;
        word-wrap: break-word
    }
}
</style>
